import {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {css, Theme, useTheme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {Container} from "@pg-design/grid";
import {calculateRemSize, flexAbsoluteCenter, mb, mt, onDesktop, ph, w100} from "@pg-design/helpers-css";
import {BrandEyeIcon, BrandMeasurementIcon, UserIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {breakpoints} from "../../../common/app/styles/breakpoints";
import {LandingInfoCard} from "../../monitoring/components/landing/Reasons";
import {
    arrowMobileImg,
    arrowMonitoringCard,
    arrowPrezentacjeCard,
    arrowResearchCard,
    DiagramArrowSvg,
    diagramDesktop,
    diagramMobile,
    hideOnDesktop,
    hideOnMobile,
    pathMonitoringArrow,
    pathPresentationsArrow,
    pathResearchArrow
} from "./BigDataHowItWorksDiagramArrows";
import {
    ARROW_NUDGE_PX,
    CARD_DESKTOP_W,
    CARD_TEXT_CONTENT_MAX_PX,
    LAPTOP_H,
    LAPTOP_W,
    PREZENTACJE_COLUMN_OFFSET_LEFT_PX
} from "./bigDataHowItWorksDiagramConstants";

import arrowMobileResearch from "../../../public/Arrow 4.svg";
import arrowMobileMonitoring from "../../../public/Arrow 7.svg";
import arrowMobilePrezentacje from "../../../public/Arrow 8.svg";
import laptopDesktop from "../../../public/laptop_map_desktop.png";
import laptopMobile from "../../../public/laptop_map_mobile.png";

/**
 * Rząd diagramu: na desktopie klaster wyrównany w prawo; od `xl` — wyśrodkowany.
 * Bez sztucznej `min-height` (wcześniej rozpychała sekcję i „padding” w pionie).
 */
const diagramRow = css`
    ${w100};
    display: flex;
    flex-direction: column;
    gap: 1.25rem;

    ${diagramDesktop(css`
        flex-direction: row;
        justify-content: flex-end;
        align-items: stretch;
        gap: 0;

        @media (min-width: ${breakpoints.xl}) {
            justify-content: center;
        }
    `)}
`;

/**
 * Klastr diagramu: mobile — Research → laptop → Prezentacje → Monitoring (`diagramColRight` ma
 * `display: contents`, kolejność przez `order`); desktop — jak wcześniej: lewa kolumna absolutna + prawa.
 */
const diagramCluster = css`
    ${w100};
    display: flex;
    flex-direction: column;
    min-width: 0;

    ${diagramMobile(css`
        align-items: stretch;
        gap: 1.25rem;
    `)}

    ${diagramDesktop(css`
        position: relative;
        flex-direction: row;
        align-items: stretch;
        align-self: stretch;
        gap: 0;
        width: auto;
        max-width: 100%;
        flex: 0 1 auto;
    `)}
`;

/** Lewa kolumna (Prezentacje): mobile `order: 3`, wąsko w prawo; desktop — absolut + offset jak dawniej. */
const diagramColLeft = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;

    ${diagramMobile(css`
        order: 3;
        align-self: flex-end;
        width: 100%;
        max-width: ${CARD_DESKTOP_W}px;
        position: relative;
    `)}

    ${diagramDesktop(css`
        position: absolute;
        left: 0;
        margin-left: ${PREZENTACJE_COLUMN_OFFSET_LEFT_PX}px;
        top: 50%;
        transform: translateY(calc(-50% - 8px));
        width: ${CARD_DESKTOP_W}px;
        z-index: 2;
        justify-content: center;
        align-items: flex-end;
        order: unset;
        align-self: unset;
        max-width: none;
    `)}
`;

/** Prawa kolumna: na mobile `contents` — dzieci w kolejce z `diagramCluster`; desktop jak wcześniej. */
const diagramColRight = css`
    min-width: 0;

    ${diagramMobile(css`
        display: contents;
    `)}

    ${diagramDesktop(css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        margin-left: calc(${CARD_DESKTOP_W}px + 1.5rem);
        gap: 1.5rem;
        flex: 0 0 auto;
    `)}
`;

const diagramResearchBlock = css`
    ${diagramMobile(css`
        order: 1;
        align-self: center;
        width: 100%;
        max-width: ${CARD_DESKTOP_W}px;
    `)}
`;

const diagramLaptopRowMobileOrder = css`
    ${diagramMobile(css`
        order: 2;
        width: 100%;
    `)}
`;

const diagramMonitoringBlock = css`
    ${diagramMobile(css`
        order: 4;
        align-self: flex-start;
        width: 100%;
        max-width: ${CARD_DESKTOP_W}px;
        margin-left: 5rem;
    `)}
`;

const shellInner = css`
    position: relative;
    width: 100%;
    max-width: ${CARD_DESKTOP_W}px;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: visible;

    ${diagramDesktop(css`
        display: block;
        align-items: stretch;
        width: ${CARD_DESKTOP_W}px;
        min-width: ${CARD_DESKTOP_W}px;
        max-width: ${CARD_DESKTOP_W}px;
    `)}
`;

/** Research / Monitoring: bazowy układ karty w prawej kolumnie (desktop diagram). */
const shellInnerRightCardDesktop = css`
    ${shellInner}
    ${diagramDesktop(css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-self: center;
    `)}
`;

/** Research (górna karta): shift w lewo. */
const shellInnerResearchDesktop = css`
    ${shellInnerRightCardDesktop}
    ${diagramDesktop(css`
        transform: translateX(-${calculateRemSize(3)});
    `)}
`;

/** Monitoring (dolna karta): shift w prawo na desktopie; na mobile karta do lewej (bez centrowania z `shellInner`). */
const shellInnerMonitoringDesktop = css`
    ${shellInnerRightCardDesktop}
    ${diagramMobile(css`
        align-items: flex-start;
    `)}
    ${diagramDesktop(css`
        transform: translateX(calc(${calculateRemSize(3)} + 8px));
    `)}
`;

const prezentacjeCardAnchor = css`
    position: relative;
    width: 100%;
    max-width: 100%;
    overflow: visible;

    ${diagramMobile(css`
        width: auto;
        margin-left: 10rem;
    `)}
`;

/** Laptop: jak mockup w Hero (`flex-end` od lg); na desktopie diagramu dodatkowo +16px w prawo względem kolumny. */
const laptopFigure = (theme: Theme) => css`
    margin: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;

    ${onDesktop(css`
        ${mt(0)};
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        justify-content: flex-end;
    }

    ${diagramDesktop(css`
        margin-left: ${ARROW_NUDGE_PX}px;
    `)}
`;

const laptopFrame = css`
    width: 100%;
    position: relative;

    ${diagramDesktop(css`
        width: ${LAPTOP_W}px;
        max-width: 100%;
        flex-shrink: 0;
    `)}
`;

const laptopImg = css`
    display: block;
    width: 100%;
    height: auto;

    ${diagramDesktop(css`
        width: ${LAPTOP_W}px;
        max-width: 100%;
        height: auto;
        object-fit: contain;
    `)}
`;

const diagramSectionHeader = css`
    ${w100};
    text-align: center;
    ${mb(6)};
    position: relative;
    z-index: 5;

    ${diagramDesktop(css`
        ${mb(8)};
    `)}
`;

const diagramSectionStack = css`
    ${w100};
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const diagramSectionCta = css``;

const limeIconBadge = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    background-color: ${theme.colors.primary};
    color: ${theme.colors.gray[700]};
`;

const LimeBadge = ({children}: {children: ReactNode}) => {
    const theme = useTheme();
    return <div css={limeIconBadge(theme)}>{children}</div>;
};

export const BigDataHowItWorks = () => {
    const {t} = useTranslation();
    const theme = useTheme();

    const cardNarrow = {
        contentMaxWidthPx: CARD_TEXT_CONTENT_MAX_PX,
        contentMaxWidthMobileOnly: true,
        noOuterMargin: true
    };

    const cardResearch = {
        title: t("landing.bigDataHowItWorks.card1.title"),
        description: t("landing.bigDataHowItWorks.card1.description"),
        desktopSpec: true,
        ...cardNarrow,
        icon: (
            <LimeBadge>
                <UserIcon size="2" />
            </LimeBadge>
        )
    };
    const cardPrezentacje = {
        title: t("landing.bigDataHowItWorks.card2.title"),
        description: t("landing.bigDataHowItWorks.card2.description"),
        desktopSpec: true,
        ...cardNarrow,
        icon: <BrandMeasurementIcon wrapperSize="4" wrapperColor={theme.colors.primary} size="4" />
    };
    const cardMonitoring = {
        title: t("landing.bigDataHowItWorks.card3.title"),
        description: t("landing.bigDataHowItWorks.card3.description"),
        desktopSpec: true,
        ...cardNarrow,
        icon: <BrandEyeIcon wrapperSize="4" wrapperColor={theme.colors.primary} size="4" />
    };

    const laptopDesktopAssetMedia = `(min-width: ${breakpoints.sm})`;

    return (
        <Container as="section">
            <div css={diagramSectionStack}>
                <div css={diagramSectionHeader}>
                    <Text variant="headline_1" align="center" as="h2">
                        {t("landing.bigDataHowItWorks.title")}
                    </Text>
                </div>

                <div css={diagramRow}>
                    <div css={diagramCluster}>
                        <div css={diagramColLeft}>
                            <div css={shellInner}>
                                <div css={prezentacjeCardAnchor}>
                                    <LandingInfoCard {...cardPrezentacje} />
                                    <span css={arrowPrezentacjeCard}>
                                        <span css={hideOnMobile}>
                                            <DiagramArrowSvg viewBox="0 0 118 65" pathD={pathPresentationsArrow} />
                                        </span>
                                        <span css={hideOnDesktop}>
                                            <img src={arrowMobilePrezentacje} alt="" />
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div css={diagramColRight}>
                            <div css={[diagramResearchBlock, shellInnerResearchDesktop]}>
                                <LandingInfoCard {...cardResearch} />
                                <span css={arrowResearchCard}>
                                    <span css={hideOnMobile}>
                                        <DiagramArrowSvg viewBox="0 0 66 119" pathD={pathResearchArrow} />
                                    </span>
                                    <span css={hideOnDesktop}>
                                        <img src={arrowMobileResearch} alt="" />
                                    </span>
                                </span>
                            </div>

                            <figure css={[diagramLaptopRowMobileOrder, laptopFigure(theme)]}>
                                <div css={laptopFrame}>
                                    <picture>
                                        <source media={laptopDesktopAssetMedia} srcSet={laptopDesktop} />
                                        <img
                                            css={laptopImg}
                                            src={laptopMobile}
                                            alt={t("landing.bigDataHowItWorks.laptopAlt")}
                                            width={LAPTOP_W}
                                            height={LAPTOP_H}
                                        />
                                    </picture>
                                </div>
                            </figure>

                            <div css={[diagramMonitoringBlock, shellInnerMonitoringDesktop]}>
                                <LandingInfoCard {...cardMonitoring} />
                                <span css={arrowMonitoringCard}>
                                    <span css={hideOnMobile}>
                                        <DiagramArrowSvg viewBox="0 0 66 128" pathD={pathMonitoringArrow} />
                                    </span>
                                    <span css={hideOnDesktop}>
                                        <img src={arrowMobileMonitoring} alt="" css={arrowMobileImg} />
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
};

const ctaLink = css`
    display: block;
    width: 100%;
    text-decoration: none;

    @media (min-width: ${breakpoints.xxs}) {
        display: inline-block;
        width: auto;
    }
`;

const ctaButton = css`
    ${ph(6)};
    text-transform: uppercase;
    font-weight: 700;
    border-radius: 9999px;
    width: 100%;
    ${mt(2)};

    @media (min-width: ${breakpoints.xxs}) {
        width: auto;
        min-width: 20.8rem;
    }
`;
